import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  App as AntApp,
  Form,
  Input,
  Button,
  Card,
  Flex,
  Radio,
  Divider,
  Typography,
  Progress,
} from "antd";
import {
  PlusOutlined,
  LeftOutlined,
  RightOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { QuizApi } from "../../../../api/quiz";
import { useAuth } from "../../../../hooks";
import { NEW_EVALUACIONES } from "../../../../ui/featureFlags";
import { initialValues } from "./QuizForm.form";

import styles from "./QuizForm.module.scss";

const { Title, Text } = Typography;

// Respuestas por pregunta: minimo 2 (permite verdadero/falso), maximo 4.
// Solo 1 correcta. El examen del alumno y el backend ya soportan N respuestas.
const MIN_ANSWERS = 2;
const MAX_ANSWERS = 4;

function QuizFormInner(props) {
  const { onClose, onReload, quiz } = props;

  const { accessToken, user } = useAuth();
  const teacherId = user?.user?.teacher;

  const { t } = useTranslation("evaluations");
  const quizApi = useMemo(() => new QuizApi(), []);
  const { message } = AntApp.useApp();

  const [saving, setSaving] = useState(false);

  // ===== 1) Form principal (name/description) =====
  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue(initialValues(quiz));
  }, [form, quiz]);

  // ===== 2) Estado para preguntas =====
  const [questionsArray, setQuestionsArray] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questionLocal, setQuestionLocal] = useState({
    question: "",
    responses: ["", ""],
    correctIndex: -1,
  });

  // Adaptación al editar
  useEffect(() => {
    if (quiz && Array.isArray(quiz.questions) && quiz.questions.length > 0) {
      const adapted = quiz.questions.map((q) => {
        const raw = (q.responses || []).map((r) => r || "");
        // Carga las respuestas reales del quiz (2, 3 o 4) sin forzar a 4.
        const responses =
          raw.length >= MIN_ANSWERS
            ? raw.slice(0, MAX_ANSWERS)
            : [...raw, "", ""].slice(0, MIN_ANSWERS);
        const idx = responses.findIndex((r) => r === q.correct);
        return {
          question: q.question || "",
          responses,
          correctIndex: idx >= 0 ? idx : -1,
        };
      });
      setQuestionsArray(adapted);
      setCurrentQuestionIndex(0);
    } else {
      setQuestionsArray([]);
      setCurrentQuestionIndex(0);
    }
  }, [quiz]);

  // Cargar la pregunta actual al cambiar el índice
  useEffect(() => {
    const curr = questionsArray[currentQuestionIndex];
    if (curr) {
      setQuestionLocal({ ...curr });
    } else {
      setQuestionLocal({
        question: "",
        responses: ["", "", "", ""],
        correctIndex: -1,
      });
    }
  }, [currentQuestionIndex, questionsArray]);

  const storePartialQuestion = useCallback(() => {
    const isEmpty =
      (!questionLocal.question || questionLocal.question.trim() === "") &&
      questionLocal.responses.every((r) => !r || r.trim() === "") &&
      questionLocal.correctIndex === -1;

    if (isEmpty) return;

    setQuestionsArray((prev) => {
      const updated = [...prev];
      updated[currentQuestionIndex] = {
        question: (questionLocal.question || "").trim(),
        responses: questionLocal.responses.map((r) => (r || "").trim()),
        correctIndex:
          typeof questionLocal.correctIndex === "number"
            ? questionLocal.correctIndex
            : -1,
      };
      return updated;
    });
  }, [currentQuestionIndex, questionLocal]);

  const totalQuestions = Math.max(questionsArray.length, 1);
  const progress = Math.round(
    ((currentQuestionIndex + 1) / totalQuestions) * 100,
  );

  const goPrev = useCallback(() => {
    if (currentQuestionIndex <= 0) return;
    storePartialQuestion();
    setCurrentQuestionIndex((i) => i - 1);
  }, [currentQuestionIndex, storePartialQuestion]);

  const goNext = useCallback(() => {
    // Solo navega entre preguntas que ya existen. Crear es tarea exclusiva de
    // "Anadir pregunta" (asi dejan de hacer lo mismo los dos botones).
    if (currentQuestionIndex >= questionsArray.length - 1) return;
    storePartialQuestion();
    setCurrentQuestionIndex((i) => i + 1);
  }, [currentQuestionIndex, questionsArray.length, storePartialQuestion]);

  const addQuestion = useCallback(() => {
    storePartialQuestion();
    setQuestionsArray((prev) => [
      ...prev,
      { question: "", responses: ["", ""], correctIndex: -1 },
    ]);
    setCurrentQuestionIndex((prev) => Math.max(prev, 0) + 1);
  }, [storePartialQuestion]);

  const removeCurrentQuestion = useCallback(() => {
    if (questionsArray.length === 0) return;

    setQuestionsArray((prev) => {
      const updated = [...prev];
      updated.splice(currentQuestionIndex, 1);
      return updated;
    });

    setCurrentQuestionIndex((prev) => Math.max(0, prev - 1));
  }, [currentQuestionIndex, questionsArray.length]);

  // ===== Respuestas variables (de 2 a 4, solo 1 correcta) =====
  const addAnswer = useCallback(() => {
    setQuestionLocal((prev) => {
      if (prev.responses.length >= MAX_ANSWERS) return prev;
      return { ...prev, responses: [...prev.responses, ""] };
    });
  }, []);

  const removeAnswer = useCallback((idx) => {
    setQuestionLocal((prev) => {
      if (prev.responses.length <= MIN_ANSWERS) return prev;
      const responses = prev.responses.filter((_, i) => i !== idx);
      let { correctIndex } = prev;
      if (correctIndex === idx) correctIndex = -1;
      else if (correctIndex > idx) correctIndex -= 1;
      return { ...prev, responses, correctIndex };
    });
  }, []);

  const validateQuestions = useCallback(
    (arr) => {
      const normalized = arr
        .map((q) => ({
          question: (q.question || "").trim(),
          responses: (q.responses || [])
            .map((r) => (r || "").trim())
            .slice(0, MAX_ANSWERS),
          correctIndex:
            typeof q.correctIndex === "number" ? q.correctIndex : -1,
        }))
        // quita preguntas completamente vacías
        .filter(
          (q) =>
            q.question !== "" ||
            q.responses.some((r) => r !== "") ||
            (q.correctIndex !== -1 && q.correctIndex !== null),
        );

      if (normalized.length === 0) {
        return { ok: false, error: t("quizForm.noQuestionWarning") };
      }

      for (let i = 0; i < normalized.length; i += 1) {
        const q = normalized[i];
        if (!q.question)
          return {
            ok: false,
            error: t("quizForm.missingStatement", { n: i + 1 }),
          };
        if (q.responses.length < MIN_ANSWERS || q.responses.some((r) => !r)) {
          return {
            ok: false,
            error: t("quizForm.missingAnswers", { n: i + 1 }),
          };
        }
        if (q.correctIndex < 0 || q.correctIndex >= q.responses.length) {
          return {
            ok: false,
            error: t("quizForm.missingCorrect", { n: i + 1 }),
          };
        }
      }

      return { ok: true, normalized };
    },
    [t],
  );

  const handleFinish = useCallback(
    async (values) => {
      try {
        setSaving(true);

        // Guarda lo que esté editando el usuario ahora mismo
        storePartialQuestion();

        // Valida preguntas con el array más actualizado posible
        const workingArray = [...questionsArray];
        workingArray[currentQuestionIndex] = {
          question: (questionLocal.question || "").trim(),
          responses: questionLocal.responses.map((r) => (r || "").trim()),
          correctIndex: questionLocal.correctIndex,
        };

        const validation = validateQuestions(workingArray);
        if (!validation.ok) {
          message.error(validation.error);
          return;
        }

        const backendQuestions = validation.normalized.map((q) => ({
          question: q.question,
          responses: q.responses,
          correct: q.responses[q.correctIndex],
        }));

        const finalData = {
          name: (values.name || "").trim(),
          description: (values.description || "").trim(),
          questions: backendQuestions,
        };

        if (!finalData.name) {
          message.error(t("quizForm.nameRequiredInline"));
          return;
        }
        if (!finalData.description) {
          message.error(t("quizForm.descriptionRequiredInline"));
          return;
        }

        if (quiz?._id) {
          await quizApi.updateQuiz(accessToken, quiz._id, finalData);
        } else {
          await quizApi.createQuiz(accessToken, teacherId, finalData);
        }

        message.success(
          quiz?._id ? t("quizForm.savedSuccess") : t("quizForm.createdSuccess"),
        );
        if (typeof onReload === "function") onReload();
        if (typeof onClose === "function") onClose();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error guardando quiz:", err);
        message.error(t("quizForm.saveError"));
      } finally {
        setSaving(false);
      }
    },
    [
      accessToken,
      currentQuestionIndex,
      message,
      onClose,
      onReload,
      questionLocal,
      questionsArray,
      quiz,
      quizApi,
      storePartialQuestion,
      t,
      teacherId,
      validateQuestions,
    ],
  );

  return (
    <div className={styles.formWrap}>
      <Card className={styles.card} classNames={{ body: styles.cardBody }}>
        <Title level={4} style={{ marginTop: 0, marginBottom: 4 }}>
          {quiz?._id ? t("quizForm.titleEdit") : t("quizForm.titleCreate")}
        </Title>
        <Text type="secondary">{t("quizForm.subtitle")}</Text>

        <Divider style={{ margin: "12px 0" }} />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          requiredMark={false}
        >
          <div className={styles.grid2}>
            <Form.Item
              label={t("quizForm.nameLabel")}
              name="name"
              rules={[{ required: true, message: t("quizForm.nameRequired") }]}
            >
              <Input size="large" placeholder={t("quizForm.namePlaceholder")} />
            </Form.Item>

            <Form.Item
              label={t("quizForm.descriptionLabel")}
              name="description"
              rules={[
                { required: true, message: t("quizForm.descriptionRequired") },
              ]}
            >
              <Input
                size="large"
                placeholder={t("quizForm.descriptionPlaceholder")}
              />
            </Form.Item>
          </div>

          <Card
            className={styles.sectionCard}
            classNames={{ body: styles.sectionBody }}
          >
            <Flex justify="space-between" align="center" gap={12} wrap="wrap">
              <div>
                <Text strong>{t("quizForm.questionsSection")}</Text>
                <div className={styles.miniMeta}>
                  <Text type="secondary">
                    {t("quizForm.questionLabel")}{" "}
                    {Math.min(currentQuestionIndex + 1, totalQuestions)}{" "}
                    {t("quizForm.questionOf")} {totalQuestions}
                  </Text>
                </div>
              </div>

              <div className={styles.progressWrap}>
                <Progress percent={progress} showInfo={false} />
              </div>
            </Flex>

            <Divider style={{ margin: "12px 0" }} />

            <Form.Item label={t("quizForm.questionLabel")} required>
              <Input.TextArea
                value={questionLocal.question}
                onChange={(e) =>
                  setQuestionLocal((prev) => ({
                    ...prev,
                    question: e.target.value,
                  }))
                }
                placeholder="Escribe aquí el enunciado..."
                autoSize={{ minRows: 2, maxRows: 4 }}
              />
            </Form.Item>

            <div className={styles.answers}>
              <Text strong>{t("quizForm.answersLabel")}</Text>
              <Text type="secondary" className={styles.answersHint}>
                {t("quizForm.markCorrect")}
              </Text>

              <Radio.Group
                value={questionLocal.correctIndex}
                onChange={(e) =>
                  setQuestionLocal((prev) => ({
                    ...prev,
                    correctIndex: e.target.value,
                  }))
                }
                className={styles.radioGroup}
              >
                {questionLocal.responses.map((val, idx) => (
                  <div key={idx} className={styles.answerRow}>
                    <Radio value={idx} />
                    <Input
                      value={val}
                      onChange={(e) => {
                        const next = [...questionLocal.responses];
                        next[idx] = e.target.value;
                        setQuestionLocal((prev) => ({
                          ...prev,
                          responses: next,
                        }));
                      }}
                      placeholder={`${t("quizForm.answerPlaceholder")} ${idx + 1}`}
                    />
                    <Button
                      type="text"
                      icon={<CloseOutlined />}
                      onClick={() => removeAnswer(idx)}
                      disabled={questionLocal.responses.length <= MIN_ANSWERS}
                      aria-label={t("quizForm.removeAnswer")}
                      title={t("quizForm.removeAnswer")}
                      className={styles.removeAnswerBtn}
                    />
                  </div>
                ))}
              </Radio.Group>

              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addAnswer}
                disabled={questionLocal.responses.length >= MAX_ANSWERS}
                className={styles.addAnswerBtn}
              >
                {t("quizForm.addAnswer")}
              </Button>
            </div>

            <Divider style={{ margin: "14px 0" }} />

            <Flex justify="space-between" align="center" wrap="wrap" gap={10}>
              <Flex gap={8} wrap="wrap">
                <Button
                  onClick={goPrev}
                  disabled={currentQuestionIndex <= 0}
                  icon={<LeftOutlined />}
                >
                  {t("quizForm.prevButton")}
                </Button>
                <Button
                  onClick={goNext}
                  disabled={currentQuestionIndex >= questionsArray.length - 1}
                  icon={<RightOutlined />}
                >
                  {t("quizForm.nextButton")}
                </Button>
              </Flex>

              <Flex gap={8} wrap="wrap">
                <Button
                  onClick={removeCurrentQuestion}
                  disabled={questionsArray.length === 0}
                  icon={<DeleteOutlined />}
                  danger={NEW_EVALUACIONES}
                  className={NEW_EVALUACIONES ? undefined : styles.dangerSoft}
                >
                  {t("quizForm.removeButton")}
                </Button>

                <Button
                  onClick={addQuestion}
                  type={NEW_EVALUACIONES ? "primary" : undefined}
                  icon={<PlusOutlined />}
                  className={NEW_EVALUACIONES ? undefined : styles.primarySoft}
                >
                  {t("quizForm.addButton")}
                </Button>
              </Flex>
            </Flex>
          </Card>

          <div className={styles.footer}>
            <Flex justify="flex-end" gap={10} wrap="wrap">
              <Button onClick={onClose} disabled={saving}>
                {t("quizForm.cancelButton")}
              </Button>
              <Button
                htmlType="submit"
                type={NEW_EVALUACIONES ? "primary" : undefined}
                loading={saving}
                icon={<SaveOutlined />}
                className={NEW_EVALUACIONES ? undefined : styles.primaryBtn}
              >
                {quiz?._id
                  ? t("quizForm.saveButton")
                  : t("quizForm.createButton")}
              </Button>
            </Flex>
          </div>
        </Form>
      </Card>
    </div>
  );
}

export function QuizForm(props) {
  return (
    <AntApp>
      <QuizFormInner {...props} />
    </AntApp>
  );
}
